import {
  Building2,
  CalendarCheck2,
  CalendarDays,
  ChartColumnIncreasing,
  ChevronDown,
  ChevronsUpDown,
  LayoutDashboard,
  LifeBuoy,
  Menu,
  MessagesSquare,
  Moon,
  PanelLeftClose,
  PanelLeftOpen,
  Settings,
  Sun,
  UsersRound,
  X,
  Zap,
} from 'lucide-react'
import {
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from 'react'
import { NavLink, Outlet, useLocation } from 'react-router-dom'

import { useAuth } from '../features/auth/AuthProvider'
import { useTenant } from '../features/tenant/TenantProvider'
import { useTheme } from '../features/theme/ThemeProvider'
import { NotificationCenter } from '../features/notifications/NotificationCenter'
import { OrganizationAlertBanner } from '../features/organization-alert/OrganizationAlertBanner'
import type { OrganizationAlertReader } from '../features/organization-alert/organization-alert-queries'
import { useOrganizationAdmin } from '../features/onboarding/useOrganizationAdmin'
import { canManageOrganization } from '../features/settings/settings-access'
import {
  createNotificationCenterRepository,
  type NotificationCenterRepository,
} from '../features/notifications/notification-center-repository'
import { Button } from '../ui/Button/Button'
import { StatePanel } from '../ui/StatePanel/StatePanel'
import { Spinner } from '../ui/Spinner/Spinner'
import styles from './DashboardLayout.module.css'

// One outline family (lucide, 1.75 stroke). Each destination gets a distinct
// glyph: conversations vs automations, appointments vs the calendar grid.
const NAV_ICON_SIZE = 18
const NAV_ICON_STROKE = 1.75

const primaryNavigation = [
  { to: '/conversations', label: 'Conversations', icon: MessagesSquare },
  { to: '/', label: 'Today’s Overview', icon: LayoutDashboard, end: true },
  { to: '/patients', label: 'Patients', icon: UsersRound },
  { to: '/appointments', label: 'Appointments', icon: CalendarCheck2 },
  { to: '/calendar', label: 'Calendar', icon: CalendarDays },
  { to: '/automated-activity', label: 'Automated Activity', icon: Zap },
  { to: '/analytics', label: 'Analytics', icon: ChartColumnIncreasing },
]

const routeMeta: Record<string, { title: string; subtitle: string }> = {
  '/': { title: 'Today’s Overview', subtitle: 'Clinic operations at a glance' },
  '/conversations': {
    title: 'Conversations',
    subtitle: 'Patient communication across Flio',
  },
  '/automated-activity': {
    title: 'Automated Activity',
    subtitle: 'Texts Flio sends to patients automatically',
  },
  '/patients': {
    title: 'Patients',
    subtitle: 'Practice patients synced through NexHealth',
  },
  '/appointments': {
    title: 'Appointments',
    subtitle: 'Clinic appointments synced through NexHealth',
  },
  '/calendar': {
    title: 'Calendar',
    subtitle: 'Clinic schedule synced through NexHealth',
  },
  '/analytics': {
    title: 'Analytics',
    subtitle: 'Evidence-backed clinic performance over time',
  },
  '/settings': {
    title: 'Settings',
    subtitle: 'Clinic and operational configuration',
  },
  '/report-issue': {
    title: 'Report an issue',
    subtitle: 'Send diagnostic context to Flio support',
  },
  '/organization/clinics/add': {
    title: 'Add clinic',
    subtitle: 'Connect a provider-verified NexHealth location',
  },
}

export function DashboardLayout({
  notificationRepository,
  organizationAlertReader,
}: {
  notificationRepository?: NotificationCenterRepository
  organizationAlertReader?: OrganizationAlertReader
}) {
  const auth = useAuth()
  const tenant = useTenant()
  const theme = useTheme()
  const organization = useOrganizationAdmin()
  const location = useLocation()
  const [mobileNavOpen, setMobileNavOpen] = useState(false)
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false)
  const [accountMenuOpen, setAccountMenuOpen] = useState(false)
  const accountMenuRef = useRef<HTMLDivElement>(null)
  const accountTriggerRef = useRef<HTMLButtonElement>(null)
  const signOutRef = useRef<HTMLButtonElement>(null)
  const layoutRef = useRef<HTMLDivElement>(null)
  const resolvedNotificationRepository = useMemo(
    () =>
      notificationRepository ??
      createNotificationCenterRepository({
        getUser: () => (auth.status === 'authenticated' ? auth.user : null),
      }),
    [auth, notificationRepository]
  )

  // Full-height pages subtract the alert row the header gains while an
  // Organization alert is shown (`--shell-alert-height`, 0px without one).
  const alertHeight = useRef(0)
  const publishAlertHeight = useCallback((height: number) => {
    alertHeight.current = Math.round(height)
    layoutRef.current?.style.setProperty(
      '--shell-alert-height',
      `${alertHeight.current}px`
    )
  }, [])
  // The banner can report before this root's ref is attached on mount.
  useLayoutEffect(
    () => publishAlertHeight(alertHeight.current),
    [publishAlertHeight]
  )

  const closeNavigation = () => setMobileNavOpen(false)
  const meta = routeMeta[location.pathname] ??
    (location.pathname.startsWith('/patients/')
      ? routeMeta['/patients']
      : undefined) ?? {
      title: 'Dashboard',
      subtitle: 'Flio clinic operations',
    }
  const accountEmail = auth.status === 'authenticated' ? auth.user.email : null
  const accountDisplayName =
    auth.status === 'authenticated'
      ? auth.user.displayName?.trim() || auth.user.email || 'Flio user'
      : 'Flio user'
  const accountInitial = accountDisplayName.slice(0, 1).toUpperCase() || 'F'
  const workspaceName =
    tenant.selectedClinic?.name ??
    (tenant.status === 'loading' ? 'Loading clinic…' : 'Clinic workspace')
  const workspaceOptions = useMemo(() => {
    const organizationNames = new Map(
      (organization.data?.clinics ?? []).map((clinic) => [
        clinic.tenantUid,
        clinic.name,
      ])
    )
    return tenant.authorizedClinics.map((clinic) => ({
      id: clinic.id,
      name: organizationNames.get(clinic.id)?.trim() || clinic.name,
    }))
  }, [organization.data?.clinics, tenant.authorizedClinics])
  const requiresWorkspaceSelection =
    tenant.status === 'ready' &&
    tenant.selectedClinic === null &&
    workspaceOptions.length > 1
  const hasNoClinicAccess =
    tenant.status === 'ready' && tenant.authorizedClinics.length === 0
  const emptyOrganizationAdmin =
    hasNoClinicAccess &&
    canManageOrganization(organization.data) &&
    organization.data.clinics.length === 0
  const organizationPage =
    location.pathname === '/settings' ||
    location.pathname === '/organization/clinics/add'
  const activeMeta =
    hasNoClinicAccess && !emptyOrganizationAdmin
      ? {
          title: 'Clinic access unavailable',
          subtitle: 'No clinic is authorized for this account',
        }
      : requiresWorkspaceSelection
        ? {
            title: 'Choose a clinic',
            subtitle: 'Select the workspace you want to open',
          }
        : meta

  useEffect(() => {
    closeNavigation()
    setAccountMenuOpen(false)
  }, [location.pathname])

  useEffect(() => {
    if (!mobileNavOpen) return
    const closeOnEscape = (event: KeyboardEvent) => {
      if (event.key === 'Escape') closeNavigation()
    }
    window.addEventListener('keydown', closeOnEscape)
    return () => window.removeEventListener('keydown', closeOnEscape)
  }, [mobileNavOpen])

  useEffect(() => {
    if (!accountMenuOpen) return

    signOutRef.current?.focus()
    const closeOnOutsidePointer = (event: PointerEvent) => {
      if (!accountMenuRef.current?.contains(event.target as Node)) {
        setAccountMenuOpen(false)
      }
    }
    const closeOnEscape = (event: KeyboardEvent) => {
      if (event.key !== 'Escape') return
      setAccountMenuOpen(false)
      accountTriggerRef.current?.focus()
    }

    document.addEventListener('pointerdown', closeOnOutsidePointer)
    window.addEventListener('keydown', closeOnEscape)
    return () => {
      document.removeEventListener('pointerdown', closeOnOutsidePointer)
      window.removeEventListener('keydown', closeOnEscape)
    }
  }, [accountMenuOpen])

  return (
    <div
      ref={layoutRef}
      className={`${styles.layout} ${sidebarCollapsed ? styles.layoutCollapsed : ''}`}
    >
      {mobileNavOpen ? (
        <button
          className={styles.scrim}
          type="button"
          aria-label="Close navigation"
          onClick={closeNavigation}
        />
      ) : null}
      <aside
        id="primary-navigation"
        className={`${styles.sidebar} ${sidebarCollapsed ? styles.sidebarCollapsed : ''} ${mobileNavOpen ? styles.sidebarOpen : ''}`}
      >
        <div className={styles.sidebarHeader}>
          <div className={styles.brandLockup}>
            <span className={styles.logoTile}>
              <img src="/brand/flio-logo.png" alt="" />
            </span>
            <span className={styles.brandCopy}>
              <strong>Flio</strong>
              <small>Clinic operations</small>
            </span>
          </div>
          <button
            className={styles.collapseButton}
            type="button"
            aria-label={
              sidebarCollapsed ? 'Expand navigation' : 'Collapse navigation'
            }
            aria-expanded={!sidebarCollapsed}
            onClick={() => setSidebarCollapsed((collapsed) => !collapsed)}
          >
            {sidebarCollapsed ? (
              <PanelLeftOpen size={17} strokeWidth={1.75} aria-hidden="true" />
            ) : (
              <PanelLeftClose size={17} strokeWidth={1.75} aria-hidden="true" />
            )}
          </button>
        </div>
        <div
          className={`${styles.workspaceContext} ${workspaceOptions.length > 1 ? styles.workspaceSwitchable : ''}`}
          title={sidebarCollapsed ? workspaceName : undefined}
        >
          <span className={styles.workspaceSignal} aria-hidden="true">
            <Building2 size={16} strokeWidth={1.75} />
          </span>
          <span className={styles.workspaceCopy}>
            <small>Current workspace</small>
            <strong>{workspaceName}</strong>
            {workspaceOptions.length > 1 ? (
              <label className={styles.workspaceSelect}>
                <span className={styles.visuallyHidden}>Select clinic</span>
                <select
                  aria-label="Select clinic"
                  value={tenant.selectedClinic?.id ?? ''}
                  onChange={(event) => tenant.selectClinic(event.target.value)}
                >
                  <option value="" disabled>
                    Choose a clinic
                  </option>
                  {workspaceOptions.map((clinic) => (
                    <option key={clinic.id} value={clinic.id}>
                      {clinic.name}
                    </option>
                  ))}
                </select>
              </label>
            ) : null}
          </span>
          {workspaceOptions.length > 1 ? (
            <ChevronsUpDown
              className={styles.workspaceChevron}
              size={15}
              strokeWidth={1.75}
              aria-hidden="true"
            />
          ) : null}
        </div>
        <p className={styles.navLabel}>Workspace</p>
        <nav className={styles.nav} aria-label="Primary navigation">
          {primaryNavigation.map(({ to, label, icon: Icon, end }) => (
            <NavLink
              key={to}
              to={to}
              end={end}
              aria-label={label}
              title={sidebarCollapsed ? label : undefined}
              className={({ isActive }) =>
                `${styles.navLink} ${isActive ? styles.active : ''}`
              }
              onClick={closeNavigation}
            >
              <Icon
                size={NAV_ICON_SIZE}
                strokeWidth={NAV_ICON_STROKE}
                aria-hidden="true"
              />
              <span className={styles.navText}>{label}</span>
            </NavLink>
          ))}
        </nav>
        <div className={styles.sidebarFooter}>
          <p className={styles.navLabel}>Manage</p>
          <NavLink
            to="/settings"
            aria-label="Settings"
            title={sidebarCollapsed ? 'Settings' : undefined}
            className={({ isActive }) =>
              `${styles.navLink} ${isActive ? styles.active : ''}`
            }
            onClick={closeNavigation}
          >
            <Settings
              size={NAV_ICON_SIZE}
              strokeWidth={NAV_ICON_STROKE}
              aria-hidden="true"
            />
            <span className={styles.navText}>Settings</span>
          </NavLink>
          <NavLink
            to="/report-issue"
            aria-label="Report an issue"
            title={sidebarCollapsed ? 'Report an issue' : undefined}
            state={{ originPathname: location.pathname }}
            className={({ isActive }) =>
              `${styles.navLink} ${isActive ? styles.active : ''}`
            }
            onClick={closeNavigation}
          >
            <LifeBuoy
              size={NAV_ICON_SIZE}
              strokeWidth={NAV_ICON_STROKE}
              aria-hidden="true"
            />
            <span className={styles.navText}>Report an issue</span>
          </NavLink>
        </div>
      </aside>
      <main className={styles.main}>
        <header className={styles.header}>
          <div className={styles.headerBar}>
            <div className={styles.headerLead}>
              <Button
                className={styles.menuButton}
                variant="secondary"
                type="button"
                aria-label={
                  mobileNavOpen ? 'Close navigation' : 'Open navigation'
                }
                aria-controls="primary-navigation"
                aria-expanded={mobileNavOpen}
                onClick={() => setMobileNavOpen((open) => !open)}
              >
                {mobileNavOpen ? (
                  <X size={18} strokeWidth={1.75} aria-hidden="true" />
                ) : (
                  <Menu size={18} strokeWidth={1.75} aria-hidden="true" />
                )}
              </Button>
              <div className={styles.heading}>
                <h1>{activeMeta.title}</h1>
                <p>{activeMeta.subtitle}</p>
              </div>
            </div>
            <div className={styles.headerActions}>
              <NotificationCenter
                tenantId={tenant.selectedClinic?.id ?? null}
                repository={resolvedNotificationRepository}
              />
              <Button
                className={styles.themeButton}
                variant="secondary"
                type="button"
                aria-label={`Switch to ${theme.theme === 'light' ? 'dark' : 'light'} theme`}
                title={`Switch to ${theme.theme === 'light' ? 'dark' : 'light'} theme`}
                onClick={theme.toggleTheme}
              >
                {theme.theme === 'light' ? (
                  <Moon size={17} strokeWidth={1.75} aria-hidden="true" />
                ) : (
                  <Sun size={17} strokeWidth={1.75} aria-hidden="true" />
                )}
              </Button>
              <div className={styles.accountMenu} ref={accountMenuRef}>
                <button
                  ref={accountTriggerRef}
                  className={styles.accountTrigger}
                  type="button"
                  aria-label={
                    accountMenuOpen ? 'Close user menu' : 'Open user menu'
                  }
                  aria-haspopup="menu"
                  aria-controls="account-menu"
                  aria-expanded={accountMenuOpen}
                  onClick={() => setAccountMenuOpen((open) => !open)}
                >
                  <span className={styles.accountAvatar} aria-hidden="true">
                    {accountInitial}
                  </span>
                  <span className={styles.accountSummary}>
                    <strong>{accountDisplayName}</strong>
                    <small>{workspaceName}</small>
                  </span>
                  <ChevronDown
                    className={styles.accountChevron}
                    size={14}
                    aria-hidden="true"
                  />
                </button>
                {accountMenuOpen ? (
                  <div
                    id="account-menu"
                    className={styles.accountPopover}
                    role="menu"
                    aria-label="Account menu"
                  >
                    <div
                      className={styles.accountMenuSection}
                      role="presentation"
                    >
                      <p className={styles.accountMenuLabel}>User identity</p>
                      <strong className={styles.accountMenuPrimary}>
                        {accountDisplayName}
                      </strong>
                      {accountEmail && accountEmail !== accountDisplayName ? (
                        <span className={styles.accountMenuSecondary}>
                          {accountEmail}
                        </span>
                      ) : null}
                    </div>
                    <div
                      className={styles.accountMenuDivider}
                      role="separator"
                    />
                    <div
                      className={styles.accountMenuSection}
                      role="presentation"
                    >
                      <p className={styles.accountMenuLabel}>Workspace</p>
                      <strong className={styles.accountMenuPrimary}>
                        {workspaceName}
                      </strong>
                    </div>
                    <div
                      className={styles.accountMenuDivider}
                      role="separator"
                    />
                    <Button
                      ref={signOutRef}
                      className={styles.signOutButton}
                      variant="ghost"
                      type="button"
                      role="menuitem"
                      onClick={() => void auth.logout()}
                    >
                      Sign out
                    </Button>
                  </div>
                ) : null}
              </div>
            </div>
          </div>
          <OrganizationAlertBanner
            tenantId={tenant.selectedClinic?.id ?? null}
            reader={organizationAlertReader}
            onHeightChange={publishAlertHeight}
          />
        </header>
        <div className={styles.content}>
          {tenant.status === 'error' && tenant.error ? (
            <p role="alert">{tenant.error}</p>
          ) : null}
          {tenant.status === 'idle' || tenant.status === 'loading' ? (
            <Spinner label="Loading clinic access" />
          ) : emptyOrganizationAdmin ? (
            organizationPage ? (
              <Outlet />
            ) : (
              <section
                className={styles.workspaceSelection}
                aria-label="Empty organization"
              >
                <span className={styles.workspaceSelectionIcon}>
                  <Building2 size={24} aria-hidden="true" />
                </span>
                <div className={styles.workspaceSelectionCopy}>
                  <span className={styles.workspaceSelectionEyebrow}>
                    Organization
                  </span>
                  <h2>{organization.data?.organization.name}</h2>
                  <p>No clinics have been added to this organization.</p>
                </div>
                <div className={styles.emptyOrganizationActions}>
                  {organization.data?.canAddClinic ? (
                    <NavLink
                      to="/organization/clinics/add"
                      className={styles.emptyOrganizationPrimary}
                    >
                      Add clinic
                    </NavLink>
                  ) : null}
                  <NavLink
                    to="/settings?section=organization"
                    className={styles.emptyOrganizationSecondary}
                  >
                    Open Organization Settings
                  </NavLink>
                </div>
              </section>
            )
          ) : hasNoClinicAccess ? (
            <StatePanel
              kind="forbidden"
              title="Clinic access is not active"
              description="This account does not currently have an authorized clinic. Return to the active manager invitation to finish access, or contact your organization administrator."
            />
          ) : requiresWorkspaceSelection ? (
            <section
              className={styles.workspaceSelection}
              aria-labelledby="workspace-selection-title"
            >
              <span className={styles.workspaceSelectionIcon}>
                <Building2 size={24} aria-hidden="true" />
              </span>
              <div className={styles.workspaceSelectionCopy}>
                <span className={styles.workspaceSelectionEyebrow}>
                  Clinic workspace
                </span>
                <h2 id="workspace-selection-title">
                  Choose a clinic workspace
                </h2>
                <p>
                  Select a clinic to continue. Only workspaces you are
                  authorized to access are shown.
                </p>
              </div>
              <div
                className={styles.workspaceSelectionOptions}
                role="group"
                aria-label="Authorized clinic workspaces"
              >
                {workspaceOptions.map((clinic) => (
                  <Button
                    key={clinic.id}
                    className={styles.workspaceSelectionButton}
                    type="button"
                    variant="secondary"
                    onClick={() => tenant.selectClinic(clinic.id)}
                  >
                    <Building2 size={18} aria-hidden="true" />
                    <span>{clinic.name}</span>
                  </Button>
                ))}
              </div>
            </section>
          ) : (
            <Outlet />
          )}
        </div>
      </main>
    </div>
  )
}
